import type { ProductRankingHistorySummary } from 'src/types/product-rankings/product-rankings-domain';

import type { MarketProduct } from './market-rankings-product';

/** 카드 위에 배지로 뜨는 변동 신호 하나 */
export type MarketSignal = {
  icon: string;
  color: string;
  bg: string;
  label: string;
};

/**
 * 순위·가격 스냅샷에서 **읽어낼 만한 변화**를 뽑는다.
 *
 * ⚠️ 순위 변동은 **직전 스냅샷 대비**(`product.rankChange`)이고, 가격·할인은 **이력
 * 전체 요약**(`summary`) 기준이라 보는 창이 다르다. 둘을 한 줄에 나란히 세우는 것은
 * "지금 무슨 일이 일어나고 있나"를 한눈에 보이려는 것이고, 정확한 기간은 각 블록이 밝힌다.
 *
 * ⚠️ 신규 진입과 순위 변동은 **셋 중 하나만** 나온다(else-if). 신규인데 상승까지 적으면
 * 같은 말을 두 번 하는 것이다.
 */
export function buildSignals({
  product,
  summary,
}: {
  product: MarketProduct | null | undefined;
  summary: ProductRankingHistorySummary | null | undefined;
}): MarketSignal[] {
  const signals: MarketSignal[] = [];
  if (!product) return signals;

  const rankDelta = product.rankDelta ?? 0;

  if (product.rankChange === 'new') {
    signals.push({ icon: 'solar:star-linear', color: 'info.main', bg: 'info.lighter', label: '신규 진입' });
  } else if (product.rankChange === 'up' && rankDelta > 0) {
    signals.push({
      icon: 'solar:arrow-up-linear',
      color: 'success.main',
      bg: 'success.lighter',
      label: `순위 ${rankDelta}계단 상승`,
    });
  } else if (product.rankChange === 'down' && rankDelta !== 0) {
    signals.push({
      icon: 'solar:arrow-down-linear',
      color: 'error.main',
      bg: 'error.lighter',
      label: `순위 ${Math.abs(rankDelta)}계단 하락`,
    });
  }

  if (summary) {
    const priceDropPct = summary.priceDropPct ?? 0;
    if (priceDropPct > 0) {
      signals.push({
        icon: 'solar:tag-price-linear',
        color: 'error.main',
        bg: 'error.lighter',
        label: `가격 ${priceDropPct}% 인하`,
      });
    } else if (priceDropPct < 0) {
      // ⚠️ 인상은 붉게 칠하지 않는다 — 나쁜 소식이 아니라 사실이다
      signals.push({
        icon: 'solar:tag-price-linear',
        color: 'text.secondary',
        bg: 'grey.100',
        label: `가격 ${Math.abs(priceDropPct)}% 인상`,
      });
    }

    if (summary.discountDelta > 0) {
      signals.push({
        icon: 'solar:sale-linear',
        color: 'error.main',
        bg: 'error.lighter',
        label: `할인 ${summary.discountDelta}%p 확대`,
      });
    }

    // ⚠️ 점이 하나뿐이면 "최고 순위"가 당연한 말이라 `days > 1`을 건다
    if (summary.days > 1 && summary.bestRank != null && product.rank != null && summary.bestRank === product.rank) {
      signals.push({
        icon: 'solar:crown-linear',
        color: 'warning.dark',
        bg: 'warning.lighter',
        label: '수집 기간 중 최고 순위',
      });
    }
  }

  return signals;
}
